import type { PersonalHubItem, PersonalHubModel } from "@/lib/derivePersonalHubModel";
import { BRAND_COLORS } from "@/lib/brandColors";
import { useIsMobile } from "@/lib/useDeviceType";
import React from "react";
import { ScrollView } from "react-native";
import { XStack, YStack } from "tamagui";
import { CaptionText, CardTitle } from "@/components/ui/Typography";
import { PersonalHubItemCard } from "./PersonalHubItemCard";
import { PERSONAL_HUB } from "./personalHubTokens";

type Props = {
  model: PersonalHubModel;
  onOpenSurvey: () => void;
};

function itemKey(item: PersonalHubItem, index: number): string {
  switch (item.kind) {
    case "continue": return `continue-${item.lesson.id}`;
    case "dashboard": return `dashboard-${item.course.id}`;
    case "setup": return "setup";
    case "purchased": return `purchased-${index}`;
  }
}

function SecondaryItems({ items, onOpenSurvey }: { items: PersonalHubItem[]; onOpenSurvey: () => void }) {
  const isMobile = useIsMobile();

  if (isMobile && items.length > 1) {
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        nestedScrollEnabled
        directionalLockEnabled
        contentContainerStyle={{ gap: PERSONAL_HUB.gap, alignItems: "stretch" }}
      >
        {items.map((item, i) => (
          <YStack key={itemKey(item, i + 1)} width={PERSONAL_HUB.mobileRailWidth}>
            <PersonalHubItemCard item={item} tile onOpenSurvey={onOpenSurvey} />
          </YStack>
        ))}
      </ScrollView>
    );
  }

  return (
    <XStack gap={PERSONAL_HUB.gap} ai="stretch">
      {items.map((item, i) => (
        <YStack
          key={itemKey(item, i + 1)}
          flexGrow={1}
          flexShrink={1}
          flexBasis={0}
          miw={0}
          alignSelf="stretch"
        >
          <PersonalHubItemCard item={item} tile onOpenSurvey={onOpenSurvey} />
        </YStack>
      ))}
    </XStack>
  );
}

export const CoursesPersonalHub = React.memo(function CoursesPersonalHub({
  model,
  onOpenSurvey,
}: Props) {
  const isMobile = useIsMobile();
  const { items, subtitle } = model;
  const [first, ...rest] = items;
  const isSetupOnly = first?.kind === "setup" && rest.length === 0;

  return (
    <YStack w="100%" mt="$1" mb="$1">
      <YStack
        w="100%"
        br={PERSONAL_HUB.panelRadius}
        borderWidth={1}
        borderColor={PERSONAL_HUB.panelBorder}
        bg={isSetupOnly ? PERSONAL_HUB.setupPanelBg : BRAND_COLORS.cardBg}
        overflow="hidden"
        boxShadow={PERSONAL_HUB.panelShadow}
      >
        <XStack px={isMobile ? "$3" : "$3.5"} pt={isMobile ? "$2.5" : "$3"} pb="$2" ai="center" gap="$2">
          <YStack w={3} h={14} br={2} bg={BRAND_COLORS.accent} flexShrink={0} />
          <YStack f={1} miw={0} gap={1}>
            <CardTitle color={BRAND_COLORS.text} letterSpacing={-0.15}>
              {PERSONAL_HUB.sectionTitle}
            </CardTitle>
            {subtitle ? (
              <CaptionText color={BRAND_COLORS.muted}>
                {subtitle}
              </CaptionText>
            ) : null}
          </YStack>
        </XStack>

        {first ? (
          <YStack
            bg={rest.length > 0 ? PERSONAL_HUB.emphasisBg : undefined}
            borderBottomWidth={rest.length > 0 ? 1 : 0}
            borderColor="rgba(0,0,0,0.05)"
          >
            <PersonalHubItemCard item={first} emphasis onOpenSurvey={onOpenSurvey} />
          </YStack>
        ) : null}

        {rest.length > 0 ? (
          <YStack px="$3" py={isMobile ? "$2" : "$3"}>
            <SecondaryItems items={rest} onOpenSurvey={onOpenSurvey} />
          </YStack>
        ) : null}
      </YStack>
    </YStack>
  );
});
